import { get } from "@src/appV2/api/api";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const TIMEKEEPING_VALIDATION_URL = "/shifts/record_timekeeping_action/validate";

const validateTimekeepingActionParamsSchema = z.object({
  editedClockIn: z.string(),
  editedClockOut: z.string(),
});

export interface UseValidateTimekeepingActionParams {
  shiftId: string;
  editedClockIn: string;
  editedClockOut: string;
}

/**
 * Validates that edited clock-in/out times don't overlap with other shifts.
 * Uses GET /api/shifts/record_timekeeping_action/validate/:shiftId.
 * Returns 200 on success, throws 400 BadRequest when overlap is detected.
 */
export function useValidateTimekeepingAction(): UseMutationResult<
  void,
  AxiosError,
  UseValidateTimekeepingActionParams
> {
  return useMutation({
    mutationFn: async (params: UseValidateTimekeepingActionParams) => {
      const { shiftId, editedClockIn, editedClockOut } = params;

      await get({
        url: `${TIMEKEEPING_VALIDATION_URL}/${shiftId}`,
        queryParams: { editedClockIn, editedClockOut },
        requestSchema: validateTimekeepingActionParamsSchema,
        responseSchema: z.unknown(),
      });
    },
  });
}
